// Vimeo: click-to-play facades that load the player SDK lazily, warm each player
// as its root scrolls into view, show the embed the instant it is clicked, pause
// each other, and pause/resume with the viewport. Falls back to opening vimeo.com
// if the film cannot be embedded here (or the SDK never arrives).
// Full-width films autoplay muted once they fill half the viewport (or all of the
// film is in view, when it is shorter than that) and get a [data-vimeo-sound] unmute
// button; muting again is left to Vimeo's own controls.
import { onReady } from '../utils/ready.js';

export function initVimeo() {
  var SDK = 'https://cdn.jsdelivr.net/npm/@vimeo/player@2.30.4/dist/player.min.js';
  var SRI = 'sha384-LbseSuOsewmQvfKC/1PIJqSPfZAnot/S4XbjKTWznWled3wqdbRGDdcNhZAtyHIS';
  var FULL_WIDTH = 0.9;
  var VIEW_SHARE = 0.5;
  var STEPS = [];
  for (var i = 0; i <= 40; i++) STEPS.push(i / 40);
  function init() {
    var roots = [].slice.call(document.querySelectorAll('[data-vimeo]'));
    if (!roots.length) return;
    var units = [];
    var sdkReady = false;
    var reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
    roots.forEach(function (root) {
      var trig = root.querySelector('[data-vimeo-trigger]');
      var mount = root.querySelector('[data-vimeo-mount]');
      var url = trig ? trig.getAttribute('href') || '' : '';
      if (!mount || !/^https?:\/\/(www\.)?vimeo\.com\/\d/.test(url)) return;
      units.push({ root: root, trig: trig, mount: mount, url: url.replace(/[?#].*$/, ''), player: null, playing: false, loading: false, blocked: false, held: false, pending: false, seen: false, auto: null, muted: false, inView: false, stopped: false, ended: false, internal: false });
    });
    if (!units.length) return;
    function quiet(pr) {
      if (pr && pr.catch) pr.catch(function () {});
    }
    function label(u, t) {
      u.trig.setAttribute('aria-label', t);
    }
    function settle(u) {
      u.loading = false;
      u.root.classList.remove('is-loading');
    }
    function setMuted(u, m) {
      u.muted = m;
      u.root.classList.toggle('is-muted', m);
    }
    // data-vimeo-autoplay="true|false" overrides the full-width test.
    function isAuto(u) {
      if (u.auto !== null) return u.auto;
      var forced = u.root.getAttribute('data-vimeo-autoplay');
      var wide = u.root.getBoundingClientRect().width >= FULL_WIDTH * document.documentElement.clientWidth;
      u.auto = forced === 'true' || (forced !== 'false' && wide && !reduced);
      if (u.auto) sound(u);
      return u.auto;
    }
    function sound(u) {
      var el = u.root.querySelector('[data-vimeo-sound]');
      if (!el) {
        el = document.createElement('button');
        el.type = 'button';
        el.className = 'vimeo-sound';
        el.setAttribute('data-vimeo-sound', '');
        u.root.appendChild(el);
      }
      el.setAttribute('aria-label', 'Unmute video');
      el.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        unmute(u);
      });
    }
    function unmute(u) {
      var p = u.player;
      if (!p || !u.muted) return;
      // iOS can refuse playback once a film is unmuted from outside its iframe;
      // fall back to playing muted rather than stopping.
      setMuted(u, false);
      u.internal = true;
      p.setMuted(false)
        .then(function () {
          return p.getVolume();
        })
        .then(function (v) {
          return v ? null : p.setVolume(1);
        })
        .then(function () {
          return u.playing || !u.inView ? null : p.play();
        })
        .catch(function () {
          setMuted(u, true);
          return p.setMuted(true).then(function () {
            return p.play();
          });
        })
        .catch(function () {})
        .then(function () {
          u.internal = false;
        });
    }
    function enough(en) {
      var vh = en.rootBounds ? en.rootBounds.height : innerHeight;
      return en.intersectionRect.height >= Math.min(VIEW_SHARE * vh, 0.98 * en.boundingClientRect.height);
    }
    function player(u) {
      if (u.player) return u.player;
      var auto = isAuto(u);
      var p = new Vimeo.Player(u.mount, auto ? { url: u.url, muted: true, playsinline: true } : { url: u.url });
      setMuted(u, auto);
      var dead = setTimeout(block, 8000);
      function block() {
        clearTimeout(dead);
        if (u.blocked || u.playing) return;
        u.blocked = true;
        settle(u);
        u.root.classList.remove('is-played', 'is-playing');
        u.mount.innerHTML = '';
        label(u, 'Watch on Vimeo');
      }
      p.ready().then(function () {
        clearTimeout(dead);
        autoplay(u);
      }, block);
      p.on('play', function () {
        u.playing = true;
        u.ended = false;
        settle(u);
        u.root.classList.add('is-played', 'is-playing');
        label(u, 'Pause video');
        units.forEach(function (o) {
          if (o !== u && o.playing && o.player) {
            o.held = false;
            o.player.pause();
          }
        });
      });
      p.on('pause', function () {
        u.playing = false;
        if (!u.held && !u.internal) u.stopped = true;
        settle(u);
        u.root.classList.remove('is-playing');
        label(u, 'Play video');
      });
      p.on('ended', function () {
        u.playing = false;
        u.held = false;
        u.ended = true;
        settle(u);
        u.root.classList.remove('is-played', 'is-playing');
        label(u, 'Play video');
      });
      p.on('volumechange', function () {
        quiet(
          p.getMuted().then(function (m) {
            setMuted(u, m);
          }),
        );
      });
      u.player = p;
      return p;
    }
    // stopped: paused by the visitor; it and ended re-arm once the film leaves the viewport.
    function autoplay(u) {
      if (!isAuto(u) || !u.inView || u.playing || u.stopped || u.ended || u.blocked || !sdkReady) return;
      var p = player(u);
      u.held = false;
      quiet(p.play());
    }
    function toggle(u) {
      if (u.blocked) {
        open(u.trig.getAttribute('href'), '_blank', 'noopener');
        return;
      }
      if (!u.playing && !u.loading) {
        u.loading = true;
        u.root.classList.add('is-loading');
        label(u, 'Loading video');
      }
      if (!sdkReady) {
        u.pending = true;
        return;
      }
      var p = player(u);
      u.held = false;
      if (u.playing) {
        quiet(p.pause());
        return;
      }
      u.stopped = false;
      if (u.muted) {
        setMuted(u, false);
        quiet(p.setMuted(false));
      }
      quiet(p.play());
    }
    function warm(u) {
      u.seen = true;
      if (u.player || u.blocked) return;
      if (sdkReady) player(u);
    }
    function arm(u) {
      u.root.classList.add('js-vimeo');
      label(u, 'Play video');
      u.root.addEventListener('click', function (e) {
        e.preventDefault();
        toggle(u);
      });
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(
          function (entries) {
            entries.forEach(function (en) {
              if (en.isIntersecting && !u.seen) warm(u);
              if (!en.isIntersecting) {
                u.inView = false;
                u.stopped = false;
                u.ended = false;
                if (u.player && u.playing) {
                  u.held = true;
                  u.player.pause();
                }
                return;
              }
              if (isAuto(u)) {
                u.inView = enough(en);
                autoplay(u);
              } else if (u.player && u.held && !u.playing) {
                u.held = false;
                quiet(u.player.play());
              }
            });
          },
          { threshold: STEPS },
        ).observe(u.root);
      }
    }
    units.forEach(arm);
    var s = document.createElement('script');
    s.src = SDK;
    s.integrity = SRI;
    s.crossOrigin = 'anonymous';
    s.onload = function () {
      if (!(window.Vimeo && window.Vimeo.Player)) return;
      sdkReady = true;
      units.forEach(function (u) {
        if (u.seen) warm(u);
        if (u.pending) {
          u.pending = false;
          toggle(u);
        }
      });
    };
    document.head.appendChild(s);
  }

  onReady(init);
}
